import type { WizardCriteriaItem } from "./CycleWizardStore";

export const MIME_LIBRARY = "application/x-rail-cycle-library";
export const MIME_ROW = "application/x-rail-cycle-row";

/** Frozen catalogue id so picks do not collide with a customized saved template. */
export const INDUSTRY_STANDARD_LIBRARY_ID = "industry-standard";

export type LibraryPayload = {
  unit_no: string;
  element_no: string;
  element: string;
  criteria: string;
  /** Present for template rows; stored on added criteria to hide the line in the list until removed */
  libraryKey?: string;
};

export type LibraryDragPayload = {
  items: LibraryPayload[];
};

export interface LibraryUnit {
  id: string;
  title: string;
  elements: { id: string; title: string; demonstrate: string[]; explain: string[] }[];
}

export interface LibraryCycle {
  id: string;
  name: string;
  /** Optional; when present, used for Cycle Info when selecting a fixed template */
  expiryYears?: number;
  /** 1 = company may customize; 0 or absent = fixed (view-only in company wizard) */
  template_editable_by_company?: number;
  units: LibraryUnit[];
}

export function stripReferenceForDisplay(text: string): string {
  return text.replace(/^[a-z][.)]\s*|^\d+[.)]\s*/i, "").trim() || text;
}

/** Compare criteria as the assessor sees them (ignore "1." / "a." prefixes and extra spaces). */
export function criteriaDisplayKey(text: string): string {
  return stripReferenceForDisplay(String(text ?? ""))
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

export function uniqueCriteriaLines(lines: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const line of lines || []) {
    const raw = String(line ?? "").trim();
    if (!raw) continue;
    const key = criteriaDisplayKey(raw);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(raw);
  }
  return out;
}

/**
 * One line per criterion on an element. If the same text is in both Demonstrate and Explain
 * (common when Explain was saved with a "1." prefix and also stored as Demonstrate), keep Explain.
 */
export function uniqueDemonstrateAndExplain(
  demonstrate: string[] | undefined,
  explain: string[] | undefined
): { demonstrate: string[]; explain: string[] } {
  const explainU = uniqueCriteriaLines(explain || []);
  const explainKeys = new Set(explainU.map(criteriaDisplayKey));
  const demonstrateU = uniqueCriteriaLines(demonstrate || []).filter((line) => !explainKeys.has(criteriaDisplayKey(line)));
  return { demonstrate: demonstrateU, explain: explainU };
}

export function makeTemplateLineKey(
  cycleId: string,
  unitId: string,
  elementId: string,
  kind: "demonstrate" | "explain",
  lineIndex: number
): string {
  return `${cycleId}|${unitId}|${elementId}|${kind}|${lineIndex}`;
}

export function isWizardCriteriaItemActive(item: WizardCriteriaItem): boolean {
  if (item.excluded) return false;
  return String(item.criteria ?? "").trim().length > 0;
}

export function activeWizardCriteriaItems(items: WizardCriteriaItem[]): WizardCriteriaItem[] {
  return items.filter(isWizardCriteriaItemActive);
}

export function buildTemplateLibraryKeyCounts(items: WizardCriteriaItem[]): Map<string, number> {
  const m = new Map<string, number>();
  for (const c of items) {
    if (c.excluded) continue;
    const k = c.libraryKey?.trim();
    if (k) m.set(k, (m.get(k) ?? 0) + 1);
  }
  return m;
}

/** Company / impersonated-company wizard: template is fixed unless system admin set it as editable. */
export function isCycleFixedForCompanyView(cycle: LibraryCycle | undefined, companyWizardContext: boolean): boolean {
  if (!companyWizardContext || !cycle) return false;
  return Number(cycle.template_editable_by_company ?? 0) !== 1;
}

export function isLibraryPayloadPickable(
  payload: LibraryPayload,
  library: LibraryCycle[],
  companyWizardContext: boolean
): boolean {
  const cid = libraryCycleIdFromLibraryKey(payload.libraryKey);
  if (!cid || cid === INDUSTRY_STANDARD_LIBRARY_ID) return true;
  const c = library.find((x) => x.id === cid);
  return !isCycleFixedForCompanyView(c, companyWizardContext);
}

export function libraryCycleIdFromLibraryKey(libraryKey?: string): string | null {
  if (!libraryKey?.trim()) return null;
  const id = libraryKey.split("|")[0]?.trim();
  return id || null;
}

export function libraryKindFromLibraryKey(libraryKey?: string): "demonstrate" | "explain" | null {
  if (!libraryKey?.trim()) return null;
  const parts = libraryKey.split("|");
  const raw = (parts[3] || "").trim().toLowerCase();
  return raw === "demonstrate" || raw === "explain" ? raw : null;
}

export function displayUnitNo(raw?: string): string {
  const s = String(raw ?? "").trim();
  if (!s) return "—";
  const m = s.match(/^u(\d+)$/i);
  return m ? m[1] : s;
}

/** "u1" / "UNIT 1 - Prepare for duty" → "Unit 1 – Prepare for duty" */
export function industryUnitDisplayTitle(unit: { id: string; title: string }): string {
  const num = unit.id.replace(/^u(\d+)$/i, "$1");
  const rest = unit.title.replace(/^unit\s*u?\d+\s*[-–:.]?\s*/i, "").trim();
  if (num && rest) return `Unit ${num} – ${rest}`;
  if (num) return `Unit ${num}`;
  return unit.title;
}

/**
 * True if this template line is already represented in the cycle (hide in library / show in outline).
 * Uses libraryKey counts when the wizard row key matches this template line exactly; otherwise falls back to
 * unit/element/criteria/type match (legacy rows without libraryKey, or draft edits keyed to the company cycle id).
 */
export function isTemplateLineClaimedInCycle(
  libraryKey: string,
  unitId: string,
  elementId: string,
  kind: "demonstrate" | "explain",
  criteriaRaw: string,
  items: WizardCriteriaItem[],
  keyCounts: Map<string, number>
): boolean {
  if ((keyCounts.get(libraryKey) ?? 0) > 0) return true;
  const normKey = criteriaDisplayKey(criteriaRaw);
  if (!normKey) return false;
  const elementIdNorm = String(elementId ?? "").trim().toLowerCase();
  return items.some((c) => {
    if (c.excluded) return false;
    if (c.type !== kind) return false;
    if (!unitNosReferToSameUnit(c.unit_no, unitId)) return false;
    if (String(c.element_no ?? "").trim().toLowerCase() !== elementIdNorm) return false;
    return criteriaDisplayKey(c.criteria ?? "") === normKey;
  });
}

export function countVisibleTemplateLinesInElement(
  cycleId: string,
  unitId: string,
  el: { id: string; demonstrate: string[]; explain: string[] },
  items: WizardCriteriaItem[],
  keyCounts: Map<string, number>
): number {
  let n = 0;
  for (let di = 0; di < el.demonstrate.length; di++) {
    const cr = el.demonstrate[di];
    const key = makeTemplateLineKey(cycleId, unitId, el.id, "demonstrate", di);
    if (!isTemplateLineClaimedInCycle(key, unitId, el.id, "demonstrate", cr, items, keyCounts)) n++;
  }
  for (let ei = 0; ei < el.explain.length; ei++) {
    const cr = el.explain[ei];
    const key = makeTemplateLineKey(cycleId, unitId, el.id, "explain", ei);
    if (!isTemplateLineClaimedInCycle(key, unitId, el.id, "explain", cr, items, keyCounts)) n++;
  }
  return n;
}

export function collectVisiblePayloadsForUnit(
  cycleId: string,
  unit: LibraryUnit,
  items: WizardCriteriaItem[],
  keyCounts: Map<string, number>
): LibraryPayload[] {
  const out: LibraryPayload[] = [];
  for (const el of unit.elements) {
    const pushIfVisible = (criteria: string, kind: "demonstrate" | "explain", idx: number) => {
      const libraryKey = makeTemplateLineKey(cycleId, unit.id, el.id, kind, idx);
      if (isTemplateLineClaimedInCycle(libraryKey, unit.id, el.id, kind, criteria, items, keyCounts)) return;
      out.push({
        unit_no: unit.id,
        element_no: el.id,
        element: el.title,
        criteria,
        libraryKey,
      });
    };
    el.demonstrate.forEach((d, di) => pushIfVisible(d, "demonstrate", di));
    el.explain.forEach((ex, ei) => pushIfVisible(ex, "explain", ei));
  }
  return out;
}

export function collectVisiblePayloadsForElement(
  cycleId: string,
  unitId: string,
  el: LibraryUnit["elements"][number],
  items: WizardCriteriaItem[],
  keyCounts: Map<string, number>
): LibraryPayload[] {
  const out: LibraryPayload[] = [];
  const pushIfVisible = (criteria: string, kind: "demonstrate" | "explain", idx: number) => {
    const libraryKey = makeTemplateLineKey(cycleId, unitId, el.id, kind, idx);
    if (isTemplateLineClaimedInCycle(libraryKey, unitId, el.id, kind, criteria, items, keyCounts)) return;
    out.push({
      unit_no: unitId,
      element_no: el.id,
      element: el.title,
      criteria,
      libraryKey,
    });
  };
  el.demonstrate.forEach((d, di) => pushIfVisible(d, "demonstrate", di));
  el.explain.forEach((ex, ei) => pushIfVisible(ex, "explain", ei));
  return out;
}

function isIndustryUnitId(id?: string): boolean {
  return /^u\d+$/i.test(String(id ?? "").trim());
}

export function industryUnitIdFromWizardUnitNo(raw?: string): string | null {
  const s = String(raw ?? "").trim();
  const prefixed = s.match(/^u(\d+)\b/i);
  if (prefixed) return `u${prefixed[1]}`;
  return null;
}

export function unitNosReferToSameUnit(a?: string, b?: string): boolean {
  const aa = String(a ?? "").trim();
  const bb = String(b ?? "").trim();
  if (!aa || !bb) return false;
  if (aa === bb) return true;
  const idA = industryUnitIdFromWizardUnitNo(aa);
  const idB = industryUnitIdFromWizardUnitNo(bb);
  if (idA && idB && idA === idB) return true;
  const pa = parseCustomUnitNo(aa);
  const pb = parseCustomUnitNo(bb);
  if (idA && pb.number && idA === `u${pb.number}`) return true;
  if (idB && pa.number && idB === `u${pa.number}`) return true;
  return !!(pa.number && pa.number === pb.number);
}

/** Keep `u1` when editing an industry unit name so catalogue ids survive save. */
export function composeWizardUnitNo(number: string, name: string, previousUnitNo?: string): string {
  const keepU = !!industryUnitIdFromWizardUnitNo(previousUnitNo);
  const n = String(number ?? "").trim();
  const nm = String(name ?? "");
  if (keepU && n) return nm ? `u${n} - ${nm}` : `u${n}`;
  return composeCustomUnitNo(n, nm);
}

/** Custom library units store the name on `title`; industry ids like `u1` keep the prefix and the title. */
export function wizardUnitNoFromLibraryUnit(unit: LibraryUnit): string {
  const id = String(unit.id ?? "").trim();
  const parsedId = parseCustomUnitNo(id);
  const parsedTitle = parseCustomUnitNo(unit.title);
  const number = parsedId.number || parsedTitle.number || (isIndustryUnitId(id) ? id.replace(/^u/i, "") : "");
  const name = (parsedId.name || parsedTitle.name).trim();
  if (isIndustryUnitId(id) || industryUnitIdFromWizardUnitNo(id)) {
    const n = number || "1";
    return name ? `u${n} - ${name}` : `u${n}`;
  }
  return composeCustomUnitNo(number, parsedId.name || parsedTitle.name) || id;
}

/** Resolve a catalogue unit id from a unit number (`1`, `u1`, or plain `"1"` ids). */
export function catalogueUnitIdForNumber(unitNumber: string, catalogue: LibraryUnit[] = []): string | null {
  const n = String(unitNumber ?? "").trim();
  if (!n) return null;
  const uPrefixed = catalogue.find((c) => c.id === `u${n}`);
  if (uPrefixed) return uPrefixed.id;
  const plain = catalogue.find((c) => c.id === n);
  if (plain) return plain.id;
  return null;
}

function catalogueUnitIndex(id: string, catalogue: LibraryUnit[] = []): number {
  const direct = catalogue.findIndex((u) => u.id === id);
  if (direct >= 0) return direct;
  const num = parseCustomUnitNo(id).number;
  if (!num) return 1000;
  const byNum = catalogue.findIndex((u) => u.id === num || u.id === `u${num}`);
  return byNum >= 0 ? byNum : 1000;
}

function catalogueUnitForId(id: string, catalogue: LibraryUnit[] = []): LibraryUnit | undefined {
  const direct = catalogue.find((u) => u.id === id);
  if (direct) return direct;
  const num = parseCustomUnitNo(id).number;
  if (!num) return undefined;
  return catalogue.find((u) => u.id === num || u.id === `u${num}`);
}

function unitCatalogueKey(unit: LibraryUnit, catalogue: LibraryUnit[] = []): string {
  const fromId = parseCustomUnitNo(unit.id);
  const fromTitle = parseCustomUnitNo(unit.title);
  const num = fromId.number || fromTitle.number;
  if (num) {
    return catalogueUnitIdForNumber(num, catalogue) || composeCustomUnitNo(num, fromId.name || fromTitle.name) || unit.id;
  }
  const name = (fromId.name || fromTitle.name || unit.title).trim().toLowerCase();
  if (name) {
    const byName = catalogue.find((c) => {
      const catName = (parseCustomUnitNo(c.title).name || c.title).trim().toLowerCase();
      return catName === name;
    });
    if (byName) return byName.id;
  }
  return unit.id;
}

function mergeLibraryElements(
  a: LibraryUnit["elements"][number],
  b: LibraryUnit["elements"][number]
): LibraryUnit["elements"][number] {
  const unique = uniqueDemonstrateAndExplain(
    [...a.demonstrate, ...b.demonstrate],
    [...a.explain, ...b.explain]
  );
  return {
    id: a.id,
    title: a.title || b.title,
    demonstrate: unique.demonstrate,
    explain: unique.explain,
  };
}

/** Merge wizard-built units that refer to the same catalogue unit (e.g. `1` vs `1 - CMS Administration`). */
export function coalesceLibraryUnitsByCatalogue(units: LibraryUnit[], catalogue: LibraryUnit[] = []): LibraryUnit[] {
  const byKey = new Map<string, LibraryUnit>();
  for (const unit of units) {
    const key = unitCatalogueKey(unit, catalogue);
    const cat = catalogueUnitForId(key, catalogue);
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        id: key,
        title: cat?.title || unit.title,
        elements: (unit.elements || []).map((el) => ({ ...el })),
      });
      continue;
    }
    const elById = new Map(existing.elements.map((el) => [String(el.id).toLowerCase(), el]));
    for (const el of unit.elements || []) {
      const elKey = String(el.id).toLowerCase();
      const prev = elById.get(elKey);
      elById.set(elKey, prev ? mergeLibraryElements(prev, el) : { ...el });
    }
    existing.elements = [...elById.values()];
    if (!parseCustomUnitNo(existing.title).name && parseCustomUnitNo(unit.title).name) {
      existing.title = unit.title;
    }
  }
  return [...byKey.values()];
}

/** Build a library-shaped cycle tree from the current wizard rows (live outline preview). */
export function libraryCycleFromWizardItems(
  items: WizardCriteriaItem[],
  template: Pick<LibraryCycle, "id" | "name" | "expiryYears" | "template_editable_by_company" | "units">
): LibraryCycle {
  const catalogue = template.units ?? [];
  const units = sortLibraryUnits(
    coalesceLibraryUnitsByCatalogue(
      wizardItemsToLibraryUnits(activeWizardCriteriaItems(items), { dropEmpty: true, catalogue }),
      catalogue
    ),
    catalogue
  );
  return {
    id: template.id,
    name: template.name,
    ...(template.expiryYears != null ? { expiryYears: template.expiryYears } : {}),
    ...(template.template_editable_by_company != null
      ? { template_editable_by_company: template.template_editable_by_company }
      : {}),
    units,
  };
}

/** Narrow an outline preview to one unit number (for custom-criteria picker). */
export function filterLibraryCycleToUnitNumber(cycle: LibraryCycle, unitNo: string): LibraryCycle {
  const num = parseCustomUnitNo(unitNo).number || String(unitNo ?? "").trim();
  if (!num) return cycle;
  const units = (cycle.units || []).filter((unit) => unitNosReferToSameUnit(unit.id, num));
  return units.length > 0 ? { ...cycle, units } : cycle;
}

/** Flatten a saved template cycle into wizard rows, including stable libraryKey for each line. */
export function flattenLibraryCycleToWizardItems(cycle: LibraryCycle): WizardCriteriaItem[] {
  const cycleId = String(cycle.id ?? "").trim();
  const items: WizardCriteriaItem[] = [];
  for (const unit of cycle.units || []) {
    const unitNo = wizardUnitNoFromLibraryUnit(unit);
    const unitId = String(unit.id ?? "").trim();
    for (const el of unit.elements || []) {
      const elementId = String(el.id ?? "").trim();
      (el.demonstrate || []).forEach((criteria, di) => {
        const raw = String(criteria ?? "").trim();
        if (!raw) return;
        items.push({
          unit_no: unitNo,
          element_no: elementId,
          element: el.title,
          criteria: raw,
          type: "demonstrate",
          libraryKey: makeTemplateLineKey(cycleId, unitId, elementId, "demonstrate", di),
        });
      });
      (el.explain || []).forEach((criteria, ei) => {
        const raw = String(criteria ?? "").trim();
        if (!raw) return;
        items.push({
          unit_no: unitNo,
          element_no: elementId,
          element: el.title,
          criteria: raw,
          type: "explain",
          libraryKey: makeTemplateLineKey(cycleId, unitId, elementId, "explain", ei),
        });
      });
    }
  }
  return items;
}

/** True when a template line is still represented in the active wizard criteria (for synced outline views). */
export function isTemplateCriteriaLineInCycle(
  cycleId: string,
  unitId: string,
  elementId: string,
  kind: "demonstrate" | "explain",
  lineIndex: number,
  criteriaText: string,
  activeItems: WizardCriteriaItem[] | undefined
): boolean {
  if (!activeItems) return true;
  const libraryKey = makeTemplateLineKey(cycleId, unitId, elementId, kind, lineIndex);
  const keyCounts = buildTemplateLibraryKeyCounts(activeItems);
  return isTemplateLineClaimedInCycle(libraryKey, unitId, elementId, kind, criteriaText, activeItems, keyCounts);
}

/** Flatten nested template units into wizard rows so the industry picker can hide lines already on the template. */
export function flattenLibraryUnitsToWizardItems(units: LibraryUnit[]): WizardCriteriaItem[] {
  const items: WizardCriteriaItem[] = [];
  for (const unit of units) {
    const unitNo = wizardUnitNoFromLibraryUnit(unit);
    for (const el of unit.elements || []) {
      const { demonstrate, explain } = uniqueDemonstrateAndExplain(el.demonstrate, el.explain);
      for (const d of demonstrate) {
        items.push({
          unit_no: unitNo,
          element_no: el.id,
          element: el.title,
          criteria: d,
          type: "demonstrate",
        });
      }
      for (const e of explain) {
        items.push({
          unit_no: unitNo,
          element_no: el.id,
          element: el.title,
          criteria: e,
          type: "explain",
        });
      }
    }
  }
  return items;
}

/** Identify a wizard row against the catalogue, so re-adding a unit does not duplicate its criteria. */
export function wizardItemLineKey(item: WizardCriteriaItem): string {
  const parsed = parseCustomUnitNo(item.unit_no);
  const unitId =
    industryUnitIdFromWizardUnitNo(item.unit_no) ||
    (parsed.number ? `u${parsed.number}` : String(item.unit_no ?? "").trim().toLowerCase());
  return `${unitId.toLowerCase()}|${String(item.element_no ?? "").trim().toLowerCase()}|${criteriaDisplayKey(item.criteria)}`;
}

/**
 * Append whole catalogue units to a custom template. Keeps the `u1` prefix so the catalogue link survives
 * a save, unless the template already uses that unit number for a different unit — then it is renumbered
 * as a plain custom unit (its element numbers follow).
 */
export function appendIndustryUnitsToWizardItems(
  items: WizardCriteriaItem[],
  units: LibraryUnit[]
): WizardCriteriaItem[] {
  const seen = new Set(items.map(wizardItemLineKey));
  const takenNumbers = new Map<string, string>();
  for (const item of items) {
    const num = parseCustomUnitNo(item.unit_no).number;
    if (num && !takenNumbers.has(num)) takenNumbers.set(num, item.unit_no);
  }

  const added: WizardCriteriaItem[] = [];
  for (const unit of units) {
    const industryId = String(unit.id ?? "").trim();
    const industryNumber = industryId.replace(/^u/i, "");
    const clash = takenNumbers.get(industryNumber);
    const sameUnit = clash ? industryUnitIdFromWizardUnitNo(clash) === industryId : false;

    let unitNo = wizardUnitNoFromLibraryUnit(unit);
    let renumberedTo = "";
    if (clash && !sameUnit) {
      renumberedTo = nextCustomUnitNumber([...items, ...added]);
      unitNo = composeCustomUnitNo(renumberedTo, parseCustomUnitNo(unitNo).name);
    }
    takenNumbers.set(parseCustomUnitNo(unitNo).number || industryNumber, unitNo);

    for (const row of flattenLibraryUnitsToWizardItems([unit])) {
      const elementNo = renumberedTo
        ? String(row.element_no ?? "").replace(new RegExp(`^${industryNumber}(?=\\D|$)`), renumberedTo)
        : row.element_no;
      const next: WizardCriteriaItem = { ...row, unit_no: unitNo, element_no: elementNo, wizardSource: "custom" };
      const key = wizardItemLineKey(next);
      if (seen.has(key)) continue;
      seen.add(key);
      added.push(next);
    }
  }

  return added;
}

/** True when this template still uses Train Driver (or other catalogue) unit ids. */
export function libraryUnitsLookIndustryBased(units: LibraryUnit[], catalogue: LibraryUnit[] = []): boolean {
  if (catalogue.length === 0) return units.some((u) => isIndustryUnitId(u.id));
  const ids = new Set(catalogue.map((u) => u.id));
  return units.some((u) => ids.has(u.id));
}

/** Fill blank industry titles from the catalogue so Unit name is not empty in the editor. */
export function restoreLibraryUnitTitles(units: LibraryUnit[], catalogue: LibraryUnit[]): LibraryUnit[] {
  return units.map((u) => {
    const cat = catalogue.find((c) => c.id === u.id);
    if (!cat) return u;
    const name = parseCustomUnitNo(u.title).name.trim();
    if (name) return u;
    return { ...u, title: cat.title };
  });
}

/** Rebuild a library unit tree from wizard rows (used when editing a custom global template). */
export function wizardItemsToLibraryUnits(
  items: WizardCriteriaItem[],
  options?: { dropEmpty?: boolean; catalogue?: LibraryUnit[] }
): LibraryUnit[] {
  const dropEmpty = options?.dropEmpty === true;
  const catalogue = options?.catalogue ?? [];
  const groups = groupWizardCriteriaByUnit(items);
  const units: LibraryUnit[] = [];
  for (const unit of groups) {
    const firstIdx = unit.elements[0]?.demonstrate[0] ?? unit.elements[0]?.explain[0];
    const raw = String(items[firstIdx]?.unit_no ?? unit.unitNo ?? "").trim();
    const fromPrefix = industryUnitIdFromWizardUnitNo(raw);
    const fromCatalogue =
      catalogue.length > 0 && unit.unitNumber ? catalogueUnitIdForNumber(unit.unitNumber, catalogue) : null;
    const id = fromPrefix || fromCatalogue || composeCustomUnitNo(unit.unitNumber, unit.unitName) || raw || "unit";
    const cat = catalogue.find((c) => c.id === id);
    const title = unit.unitName
      ? isIndustryUnitId(id) && unit.unitNumber
        ? `UNIT ${unit.unitNumber} - ${unit.unitName}`
        : unit.unitName
      : cat?.title || id;
    const elements = unit.elements
      .map((el) => {
        const demonstrate = el.demonstrate.map((i) => String(items[i]?.criteria ?? ""));
        const explain = el.explain.map((i) => String(items[i]?.criteria ?? ""));
        const unique = uniqueDemonstrateAndExplain(demonstrate, explain);
        const elementTitle = el.elementTitle || el.elementNo || "";
        return {
          id: libraryElementIdFromWizardGroup(el),
          title: elementTitle,
          demonstrate: dropEmpty ? unique.demonstrate.filter((t) => t.trim()) : unique.demonstrate,
          explain: dropEmpty ? unique.explain.filter((t) => t.trim()) : unique.explain,
        };
      })
      .filter((el) => !dropEmpty || el.demonstrate.length > 0 || el.explain.length > 0);
    if (!dropEmpty || elements.length > 0) units.push({ id, title, elements });
  }
  return units;
}

function numericIdParts(id: string): number[] {
  return (String(id).match(/\d+/g) || []).map(Number);
}

function compareNumericIds(a: string, b: string): number {
  const pa = numericIdParts(a);
  const pb = numericIdParts(b);
  const n = Math.max(pa.length, pb.length);
  for (let i = 0; i < n; i++) {
    const da = pa[i] ?? 0;
    const db = pb[i] ?? 0;
    if (da !== db) return da - db;
  }
  return a.localeCompare(b);
}

/** Letter element codes (A–H) before numeric codes (01, 02) on templates like SCA. */
function compareRailElementIds(a: string, b: string): number {
  const aa = String(a ?? "").trim();
  const bb = String(b ?? "").trim();
  const aAlpha = /^[A-Za-z]+$/.test(aa);
  const bAlpha = /^[A-Za-z]+$/.test(bb);
  if (aAlpha && bAlpha) return aa.localeCompare(bb, undefined, { sensitivity: "base" });
  if (aAlpha && !bAlpha) return -1;
  if (!aAlpha && bAlpha) return 1;
  return compareNumericIds(aa, bb);
}

function insertByCatalogueOrder<T extends { id: string }>(
  list: T[],
  item: T,
  orderOf: (id: string) => number
): void {
  const order = orderOf(item.id);
  const idx = list.findIndex((existing) => orderOf(existing.id) > order);
  if (idx < 0) list.push(item);
  else list.splice(idx, 0, item);
}

/** Units 1→10 and elements 4.1→4.2, matching the industry catalogue when present. */
export function sortLibraryUnits(units: LibraryUnit[], catalogue: LibraryUnit[] = []): LibraryUnit[] {
  const sortedUnits = [...units].sort((a, b) => {
    const ia = catalogueUnitIndex(a.id, catalogue);
    const ib = catalogueUnitIndex(b.id, catalogue);
    if (ia !== ib) return ia - ib;
    return compareNumericIds(a.id, b.id);
  });
  return sortedUnits.map((unit) => {
    return {
      ...unit,
      elements: [...(unit.elements || [])].sort((a, b) => compareRailElementIds(a.id, b.id)),
    };
  });
}

/**
 * Merge industry-library picks into a template unit tree (create missing units/elements, skip duplicates).
 */
export function mergeLibraryPayloadsIntoUnits(
  units: LibraryUnit[],
  payloads: LibraryPayload[],
  catalogue: LibraryUnit[],
  forceType?: "demonstrate" | "explain"
): LibraryUnit[] {
  const next: LibraryUnit[] = JSON.parse(JSON.stringify(units)) as LibraryUnit[];
  const catUnit = (id: string) => catalogue.find((u) => u.id === id);
  const unitOrder = (id: string) => {
    const i = catalogue.findIndex((u) => u.id === id);
    return i < 0 ? 1000 : i;
  };
  const elementOrder = (unitId: string, elementId: string) => {
    const u = catUnit(unitId);
    const i = u?.elements.findIndex((e) => e.id === elementId) ?? -1;
    return i < 0 ? 1000 : i;
  };

  for (const p of payloads) {
    const criteria = String(p.criteria ?? "").trim();
    const unitId = String(p.unit_no ?? "").trim();
    const elementId = String(p.element_no ?? "").trim();
    if (!criteria || !unitId || !elementId) continue;
    const kind = forceType ?? libraryKindFromLibraryKey(p.libraryKey) ?? "demonstrate";

    let unit = next.find((u) => u.id === unitId);
    if (!unit) {
      unit = {
        id: unitId,
        title: catUnit(unitId)?.title ?? unitId,
        elements: [],
      };
      insertByCatalogueOrder(next, unit, unitOrder);
    }

    let el = unit.elements.find((e) => e.id === elementId);
    if (!el) {
      const catEl = catUnit(unitId)?.elements.find((e) => e.id === elementId);
      el = {
        id: elementId,
        title: catEl?.title ?? p.element,
        demonstrate: [],
        explain: [],
      };
      insertByCatalogueOrder(unit.elements, el, (id) => elementOrder(unitId, id));
    }

    const arr = el[kind];
    const key = criteriaDisplayKey(criteria);
    const already = [...el.demonstrate, ...el.explain].some((line) => criteriaDisplayKey(line) === key);
    if (!already) {
      arr.push(p.criteria);
    }
  }
  return sortLibraryUnits(next, catalogue);
}

/** Parse a custom-build unit_no like "1", "u1", or "1 - Prepare for duty". */
export function parseCustomUnitNo(raw?: string): { number: string; name: string } {
  const start = String(raw ?? "").trimStart();
  if (!start) return { number: "", name: "" };

  const withSep = start.match(/^(\d+)\s+[-–:.]\s*([\s\S]*)$/);
  if (withSep) return { number: withSep[1], name: withSep[2] };

  const withSpace = start.match(/^(\d+)\s+([\s\S]+)$/);
  if (withSpace) return { number: withSpace[1], name: withSpace[2] };

  const numOnly = start.match(/^(\d+)\s*$/);
  if (numOnly) return { number: numOnly[1], name: "" };

  const unitWordSep = start.match(/^unit\s+(\d+)\s*[-–:·.]\s*([\s\S]*)$/i);
  if (unitWordSep) return { number: unitWordSep[1], name: unitWordSep[2] };

  const unitWordOnly = start.match(/^unit\s+(\d+)\s*$/i);
  if (unitWordOnly) return { number: unitWordOnly[1], name: "" };

  const unitSep = start.match(/^u(\d+)\s+[-–:.]\s*([\s\S]*)$/i);
  if (unitSep) return { number: unitSep[1], name: unitSep[2] };

  const unitSpace = start.match(/^u(\d+)\s+([\s\S]+)$/i);
  if (unitSpace) return { number: unitSpace[1], name: unitSpace[2] };

  const unitOnly = start.match(/^u(\d+)\s*$/i);
  if (unitOnly) return { number: unitOnly[1], name: "" };

  // No leading unit number — keep the whole value as the name (never as the number).
  return { number: "", name: start };
}

export function composeCustomUnitNo(number: string, name: string): string {
  const n = String(number ?? "").trim();
  const nm = String(name ?? "");
  if (!n) return nm;
  if (!nm) return n;
  return `${n} - ${nm}`;
}

/** Heading for custom units: the typed unit name, not "2 - CMSAdmin". */
export function customUnitDisplayTitle(unit: { id?: string; title?: string }): string {
  const fromTitle = parseCustomUnitNo(unit.title);
  const fromId = parseCustomUnitNo(unit.id);
  const name = (fromTitle.name || fromId.name).trim();
  const number = fromTitle.number || fromId.number;
  if (name) return name;
  if (number) return `Unit ${number}`;
  const fallback = String(unit.title || unit.id || "").trim();
  return fallback || "Unit";
}

export function customUnitGroupKey(unitNo?: string): string {
  const { number, name } = parseCustomUnitNo(unitNo);
  if (number) return `n:${number}`;
  const rest = name || String(unitNo ?? "").trim();
  if (rest) return `s:${rest.toLowerCase()}`;
  return "__ungrouped__";
}

export function customElementGroupKey(elementNo?: string, elementTitle?: string): string {
  const no = String(elementNo ?? "").trim().toLowerCase();
  const title = String(elementTitle ?? "").trim().toLowerCase();
  if (!no && !title) return "__new_element__";
  return `${no}|${title}`;
}

/** Internal element ids used before the user assigns an element number — hide in outline UI. */
export function isInternalLibraryElementId(id?: string): boolean {
  const s = String(id ?? "").trim();
  return !s || s === "__new_element__" || s.startsWith("row-") || s === "element" || s === "custom";
}

export function libraryElementIdFromWizardGroup(el: ScratchElementGroup): string {
  const no = String(el.elementNo ?? "").trim();
  if (no) return no;
  const idx = el.demonstrate[0] ?? el.explain[0];
  return typeof idx === "number" ? `row-${idx}` : "custom";
}

export function nextCustomUnitNumber(items: WizardCriteriaItem[]): string {
  let max = 0;
  for (const item of items) {
    const n = Number(parseCustomUnitNo(item.unit_no).number);
    if (Number.isInteger(n) && n > max) max = n;
  }
  return String(max + 1);
}

export function nextCustomElementNo(items: WizardCriteriaItem[], unitGroupKey: string): string {
  const inUnit = items.filter((item) => customUnitGroupKey(item.unit_no) === unitGroupKey);
  const unitNum = parseCustomUnitNo(inUnit[0]?.unit_no).number || parseCustomUnitNo(unitGroupKey.replace(/^n:/, "")).number;
  let max = 0;
  for (const item of inUnit) {
    const raw = String(item.element_no ?? "").trim();
    const m = raw.match(/(\d+)(?:\s*$)/);
    const n = m ? Number(m[1]) : Number(raw);
    if (Number.isInteger(n) && n > max) max = n;
  }
  const next = max + 1;
  return unitNum ? `${unitNum}.${next}` : String(next);
}

export type CustomCriteriaInsert = {
  unit_no: string;
  element_no: string;
  element: string;
  criteria: string;
  type: "demonstrate" | "explain";
};

function parseNumericElementNo(raw?: string): { value: number; width: number } | null {
  const s = String(raw ?? "").trim();
  if (!/^\d+$/.test(s)) return null;
  return { value: Number(s), width: s.length };
}

function formatNumericElementNo(value: number, width: number): string {
  const minWidth = Math.max(width, String(value).length);
  return String(value).padStart(minWidth, "0");
}

function wizardItemMatchesUnitAndType(
  item: WizardCriteriaItem,
  unitNo: string,
  type: "demonstrate" | "explain"
): boolean {
  if (item.excluded) return false;
  if (item.type !== type) return false;
  return unitNosReferToSameUnit(item.unit_no, unitNo);
}

/**
 * Insert a custom row at a numeric element number, shifting later rows in the same unit and column
 * (Demonstrate or Explain) so element numbers stay unique and sequential.
 */
export function insertCustomWizardCriteriaWithRenumber(
  items: WizardCriteriaItem[],
  insert: CustomCriteriaInsert,
  options?: { clientKey?: string }
): { items: WizardCriteriaItem[]; insertedIndex: number } {
  const insertParsed = parseNumericElementNo(insert.element_no);
  const newRow: WizardCriteriaItem = {
    unit_no: insert.unit_no,
    element_no: insert.element_no.trim(),
    element: insert.element,
    criteria: insert.criteria,
    type: insert.type,
    status: "Competent",
    wizardSource: "custom",
    ...(options?.clientKey ? { clientKey: options.clientKey } : {}),
  };

  if (!insertParsed) {
    const next = [...items, newRow];
    return { items: next, insertedIndex: next.length - 1 };
  }

  let padWidth = insertParsed.width;
  for (const item of items) {
    if (!wizardItemMatchesUnitAndType(item, insert.unit_no, insert.type)) continue;
    const parsed = parseNumericElementNo(item.element_no);
    if (parsed) padWidth = Math.max(padWidth, parsed.width);
  }

  const insertAt = insertParsed.value;
  const normalizedInsertNo = formatNumericElementNo(insertAt, padWidth);
  newRow.element_no = normalizedInsertNo;

  const hasCollision = items.some((item) => {
    if (!wizardItemMatchesUnitAndType(item, insert.unit_no, insert.type)) return false;
    return parseNumericElementNo(item.element_no)?.value === insertAt;
  });

  const next = items.map((item) => {
    if (!hasCollision || !wizardItemMatchesUnitAndType(item, insert.unit_no, insert.type)) return item;
    const parsed = parseNumericElementNo(item.element_no);
    if (!parsed || parsed.value < insertAt) return item;
    return {
      ...item,
      element_no: formatNumericElementNo(parsed.value + 1, padWidth),
    };
  });

  let insertedIndex = next.length;
  for (let i = 0; i < next.length; i++) {
    const item = next[i];
    if (!wizardItemMatchesUnitAndType(item, insert.unit_no, insert.type)) continue;
    const parsed = parseNumericElementNo(item.element_no);
    if (parsed && parsed.value >= insertAt) {
      insertedIndex = i;
      break;
    }
  }
  next.splice(insertedIndex, 0, newRow);
  return { items: next, insertedIndex };
}

export type ScratchElementGroup = {
  elementKey: string;
  elementNo: string;
  elementTitle: string;
  demonstrate: number[];
  explain: number[];
};

export type ScratchUnitGroup = {
  unitKey: string;
  unitNo: string;
  unitNumber: string;
  unitName: string;
  elements: ScratchElementGroup[];
};

/** Group wizard rows into unit → element → Demonstrate/Explain, matching saved cycle structure. */
export function groupWizardCriteriaByUnit(items: WizardCriteriaItem[]): ScratchUnitGroup[] {
  const units: ScratchUnitGroup[] = [];
  const byUnit = new Map<string, ScratchUnitGroup>();
  const elementMaps = new Map<string, Map<string, ScratchElementGroup>>();

  items.forEach((item, index) => {
    const unitKey = customUnitGroupKey(item.unit_no);
    let unit = byUnit.get(unitKey);
    if (!unit) {
      const parsed = parseCustomUnitNo(item.unit_no);
      unit = {
        unitKey,
        unitNo: String(item.unit_no ?? ""),
        unitNumber: parsed.number,
        unitName: parsed.name,
        elements: [],
      };
      byUnit.set(unitKey, unit);
      units.push(unit);
      elementMaps.set(unitKey, new Map());
    } else if (!unit.unitName) {
      const parsed = parseCustomUnitNo(item.unit_no);
      if (parsed.name) {
        unit.unitName = parsed.name;
        unit.unitNo = String(item.unit_no ?? "");
      }
    }

    const elMap = elementMaps.get(unitKey)!;
    const elementKey = customElementGroupKey(item.element_no, item.element);
    let el = elMap.get(elementKey);
    if (!el) {
      el = {
        elementKey,
        elementNo: String(item.element_no ?? ""),
        elementTitle: String(item.element ?? ""),
        demonstrate: [],
        explain: [],
      };
      elMap.set(elementKey, el);
      unit.elements.push(el);
    } else if (!el.elementTitle && item.element) {
      el.elementTitle = item.element;
    }

    if (item.type === "explain") el.explain.push(index);
    else el.demonstrate.push(index);
  });

  units.sort((a, b) => {
    const na = Number(a.unitNumber);
    const nb = Number(b.unitNumber);
    const aNum = Number.isInteger(na);
    const bNum = Number.isInteger(nb);
    if (aNum && bNum && na !== nb) return na - nb;
    if (aNum && !bNum) return -1;
    if (!aNum && bNum) return 1;
    return a.unitNo.localeCompare(b.unitNo);
  });
  return units;
}

export function countWizardCycleStats(items: WizardCriteriaItem[]): { units: number; criteria: number } {
  const active = activeWizardCriteriaItems(items);
  return {
    units: groupWizardCriteriaByUnit(active).length,
    criteria: active.length,
  };
}

/** Explain rows are stored with a leading "1. " so later views can tell them apart from Demonstrate. */
export function encodeCriteriaTypeForSave(text: string, type: "demonstrate" | "explain"): string {
  const t = String(text ?? "").trim();
  if (!t) return t;
  if (type === "explain" && !/^\d+\./.test(t)) return `1. ${t}`;
  return t;
}

function cleanUnitLabelForLibrary(raw: string): string {
  const s = (raw ?? "").trim();
  if (!s) return "—";
  const m = s.match(/^u(\d+)$/i);
  if (m) return `Unit ${m[1]}`;
  const numberedName = s.match(/^(\d+)\s+[-–:.]\s*(.+)$/);
  if (numberedName) return numberedName[2].trim() || `Unit ${numberedName[1]}`;
  const m2 = s.match(/^unit\s*u?(\d+)\s*[-–:.]?\s*(.*)$/i);
  if (m2) return `Unit ${m2[1]}${m2[2]?.trim() ? " · " + m2[2].trim() : ""}`.trim();
  return s;
}

/** Map GET /api/cycles/:id detail into the same shape used by template criteria preview. */
export function libraryCycleFromApiDetail(detail: Record<string, unknown> | null | undefined): LibraryCycle | null {
  if (!detail || typeof detail !== "object") return null;
  const id = String(detail.id ?? "").trim();
  if (!id) return null;
  const name = String(detail.title ?? detail.name ?? `Cycle ${id}`).trim() || `Cycle ${id}`;
  const rows = Array.isArray(detail.criteria) ? detail.criteria : [];
  type ElementBucket = { id: string; title: string; demonstrate: string[]; explain: string[]; seen: Set<string> };
  type UnitBucket = {
    id: string;
    title: string;
    elements: ElementBucket[];
    byElementKey: Map<string, ElementBucket>;
    autoElement: number;
  };
  const units: UnitBucket[] = [];
  const byUnitKey = new Map<string, UnitBucket>();
  for (const raw of rows) {
    const r = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    const rawUnit = typeof r.unit_no === "string" ? r.unit_no.trim() : String(r.unit_no ?? "").trim();
    const rawElementNo = typeof r.element_no === "string" ? r.element_no.trim() : String(r.element_no ?? "").trim();
    const elementTitle = String(r.element ?? "").trim() || rawElementNo || "Element";
    const criteriaText = String(r.criteria ?? "").trim();
    if (!criteriaText) continue;
    const displayKey = criteriaDisplayKey(criteriaText);
    if (!displayKey) continue;
    const mode = String(r.type ?? "").trim().toLowerCase() === "explain" ? "explain" : "";
    const inferredType = mode || (/^\d+\./.test(criteriaText) ? "explain" : "demonstrate");
    const unitKey = rawUnit || "__custom_unit__";
    let unit = byUnitKey.get(unitKey);
    if (!unit) {
      const unitId = rawUnit || "u-custom";
      const unitTitle = rawUnit ? cleanUnitLabelForLibrary(rawUnit) : "Unit custom";
      unit = { id: unitId, title: unitTitle, elements: [], byElementKey: new Map(), autoElement: 1 };
      byUnitKey.set(unitKey, unit);
      units.push(unit);
    }
    const elementKey = `${rawElementNo || "__custom_element__"}|${elementTitle.toLowerCase()}`;
    let element = unit.byElementKey.get(elementKey);
    if (!element) {
      const elementId = rawElementNo || `custom-${unit.autoElement++}`;
      element = { id: elementId, title: elementTitle, demonstrate: [], explain: [], seen: new Set() };
      unit.byElementKey.set(elementKey, element);
      unit.elements.push(element);
    }
    if (element.seen.has(displayKey)) {
      if (inferredType === "explain" && !element.explain.some((t) => criteriaDisplayKey(t) === displayKey)) {
        element.demonstrate = element.demonstrate.filter((t) => criteriaDisplayKey(t) !== displayKey);
        element.explain.push(criteriaText);
      }
      continue;
    }
    element.seen.add(displayKey);
    if (inferredType === "explain") element.explain.push(criteriaText);
    else element.demonstrate.push(criteriaText);
  }
  const libraryUnits: LibraryUnit[] = units
    .map((u) => ({
      id: u.id,
      title: u.title,
      elements: u.elements
        .map((el) => uniqueDemonstrateAndExplain(el.demonstrate, el.explain))
        .map((unique, idx) => ({
          id: u.elements[idx].id,
          title: u.elements[idx].title,
          demonstrate: unique.demonstrate,
          explain: unique.explain,
        }))
        .filter((el) => el.demonstrate.length > 0 || el.explain.length > 0),
    }))
    .filter((u) => u.elements.length > 0);
  if (libraryUnits.length === 0) return null;
  const expiryYears = Number(detail.expiry_years);
  return {
    id,
    name,
    ...(Number.isInteger(expiryYears) && expiryYears >= 1 ? { expiryYears } : {}),
    units: libraryUnits,
  };
}
